import React, { useEffect, useMemo, useState } from 'react'
import { Container, Col, Row, ButtonToolbar } from 'react-bootstrap'
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from 'react-beautiful-dnd'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/pro-solid-svg-icons'
import { Tooltip } from 'antd'
import TextInput from 'components/ui/forms/field/text-input/TextInput'
import RequiredFieldLabel from 'components/reusables/requiredFieldLabel'
import Button from 'design-system/button'
import { ButtonVariant, ButtonSizes } from 'design-system/button/src/Button'
import { IMainState } from 'store/states'
import * as fromActions from 'store/actions/blocks.action'
import { ISessionItemModel } from 'models/block.model'
import { getBrowserTimezone } from 'helpers/timezone.helper'
import {
  buildEmptySession,
  createSessionFormBaseline,
  getSessionAcceptDisabledReason,
  normalizeSessions,
  SessionFormBaseline,
} from 'helpers/sessionBlock.helpers'
import { SessionCard } from './SessionCard'
import styles from './Session.module.scss'

interface ISession {
  onCancel: () => void
  editing: boolean
}

const reorderSessions = (
  sessions: ISessionItemModel[],
  startIndex: number,
  endIndex: number
): ISessionItemModel[] => {
  const result = Array.from(sessions)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)
  return result.map((session, index) => ({ ...session, position: index }))
}

const Session: React.FC<ISession> = ({ onCancel }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { edit } = useSelector(({ blocks }: IMainState) => blocks)
  const translationPrefix = 'administration.blocks.type.sessions'

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [sessions, setSessions] = useState<ISessionItemModel[]>([
    buildEmptySession(0),
  ])
  const [baseline, setBaseline] = useState<SessionFormBaseline | null>(null)
  const [expandedSessions, setExpandedSessions] = useState<
    Record<number, boolean>
  >({ 0: true })
  const [editingTitleIndex, setEditingTitleIndex] = useState<number | null>(
    null
  )

  useEffect(() => {
    if (!edit) return
    const blockName = edit.name || edit.blockInstanceable?.name || ''
    const blockDescription =
      edit.description || edit.blockInstanceable?.description || ''
    const normalized = normalizeSessions(edit.blockInstanceable?.sessions)
    setName(blockName)
    setDescription(blockDescription)
    setSessions(normalized)
    setBaseline(
      createSessionFormBaseline(blockName, blockDescription, normalized)
    )
    setExpandedSessions(
      normalized.reduce<Record<number, boolean>>((acc, _, index) => {
        acc[index] = index === 0
        return acc
      }, {})
    )
  }, [edit])

  const isEditingExistingBlock = Boolean(edit?.id)

  const validationContext = useMemo(
    () => ({
      isEditingExistingBlock,
      baseline,
    }),
    [isEditingExistingBlock, baseline]
  )

  const acceptDisabledReason = useMemo(
    () =>
      getSessionAcceptDisabledReason(
        name,
        description,
        sessions,
        validationContext
      ),
    [name, description, sessions, validationContext]
  )

  const canSave = acceptDisabledReason == null

  const acceptDisabledTooltip = useMemo(() => {
    if (!acceptDisabledReason) return ''
    const { key, sessionNumber } = acceptDisabledReason
    return t(`${translationPrefix}.acceptDisabled.${key}`, {
      sessionNumber,
    })
  }, [acceptDisabledReason, t])

  const updateSession = (index: number, patch: Partial<ISessionItemModel>) => {
    setSessions((prev) =>
      prev.map((session, i) =>
        i === index ? { ...session, ...patch, position: i } : session
      )
    )
  }

  const addSession = () => {
    setSessions((prev) => {
      const next = [...prev, buildEmptySession(prev.length)]
      setExpandedSessions((current) => ({
        ...Object.keys(current).reduce<Record<number, boolean>>((acc, key) => {
          acc[Number(key)] = false
          return acc
        }, {}),
        [next.length - 1]: true,
      }))
      setEditingTitleIndex(next.length - 1)
      return next
    })
  }

  const removeSession = (index: number) => {
    setSessions((prev) => {
      if (prev.length <= 1) return prev
      return prev
        .filter((_, i) => i !== index)
        .map((session, i) => ({ ...session, position: i }))
    })
    setExpandedSessions((prev) => {
      const next: Record<number, boolean> = {}
      Object.entries(prev).forEach(([key, value]) => {
        const currentIndex = Number(key)
        if (currentIndex < index) next[currentIndex] = value
        if (currentIndex > index) next[currentIndex - 1] = value
      })
      if (Object.keys(next).length === 0) next[0] = true
      return next
    })
    setEditingTitleIndex(null)
  }

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return
    const { source, destination } = result
    if (source.index === destination.index) return

    setSessions((prev) => {
      const reordered = reorderSessions(prev, source.index, destination.index)
      setExpandedSessions((expandedPrev) => {
        const flags = prev.map((_, i) => expandedPrev[i] ?? false)
        const [removed] = flags.splice(source.index, 1)
        flags.splice(destination.index, 0, removed)
        return flags.reduce<Record<number, boolean>>((acc, value, idx) => {
          acc[idx] = value
          return acc
        }, {})
      })
      return reordered
    })
  }

  const handleOnSave = () => {
    if (!canSave) return
    const { id, lessonId, order } = edit
    const sessionsPayload = sessions.map((session, index) => ({
      ...session,
      name:
        session.name.trim() ||
        t(`${translationPrefix}.sessionLabel`, { number: index + 1 }),
      duration: Number(session.duration),
      position: index,
    }))

    if (id) {
      dispatch({
        type: fromActions.EDIT_SESSION_BLOCK,
        payload: {
          id,
          lessonId,
          name,
          description,
          sessions: sessionsPayload,
        },
      })
    } else if (lessonId) {
      dispatch({
        type: fromActions.SESSION_BLOCK_CREATE,
        data: {
          block_session: {
            name,
            description,
            timezone: getBrowserTimezone(),
            sessions: sessionsPayload,
          },
          block_instance: {
            name,
            description,
            mandatory: false,
            order: order || 1,
            visible: true,
            lesson_id: lessonId,
          },
        },
      })
    }
    onCancel()
  }

  const acceptButton = (
    <Button
      size={ButtonSizes.Small}
      onClick={handleOnSave}
      disabled={!canSave}
      data-test-id="button-session-save"
    >
      {t(`${translationPrefix}.buttons.accept`)}
    </Button>
  )

  return (
    <Container fluid data-test-id="panel-block-session">
      <Row>
        <Col xs={12} className="p-0">
          <h1 className={styles.title}>
            {t(`${translationPrefix}.blockTitle`)}
          </h1>
          <p className={styles.subtitle}>
            {t(`${translationPrefix}.blockSubtitle`)}
          </p>
        </Col>
      </Row>

      <Row>
        <Col xs={12} className={`p-0 pt-3 ${styles.formField}`}>
          <RequiredFieldLabel required className={styles.sessionFieldLabel}>
            {t(`${translationPrefix}.fields.name`)}
          </RequiredFieldLabel>
          <TextInput
            name="name"
            value={name}
            label=""
            placeHolder={t(`${translationPrefix}.placeholders.writeHere`)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setName(e.target.value)
            }
            error=""
            classes={{ input: styles.sessionInput }}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={12} className={`p-0 pt-3 ${styles.formField}`}>
          <RequiredFieldLabel className={styles.sessionFieldLabel}>
            {t(`${translationPrefix}.fields.description`)}
          </RequiredFieldLabel>
          <TextInput
            name="description"
            value={description}
            label=""
            placeHolder={t(`${translationPrefix}.placeholders.writeHere`)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setDescription(e.target.value)
            }
            error=""
            classes={{ input: styles.sessionInput }}
          />
        </Col>
      </Row>

      <div className={styles.sessionsList}>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="session-cards">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {sessions.map((session, index) => (
                  <Draggable
                    key={`session-${index}-${session.position}`}
                    draggableId={`session-${index}`}
                    index={index}
                  >
                    {(draggableProvided) => (
                      <div
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                      >
                        <SessionCard
                          session={session}
                          index={index}
                          isExpanded={expandedSessions[index] ?? false}
                          isEditingTitle={editingTitleIndex === index}
                          canRemove={sessions.length > 1}
                          dragHandleProps={draggableProvided.dragHandleProps}
                          onToggleExpand={() =>
                            setExpandedSessions((prev) => ({
                              ...prev,
                              [index]: !prev[index],
                            }))
                          }
                          onToggleEditTitle={() =>
                            setEditingTitleIndex((current) =>
                              current === index ? null : index
                            )
                          }
                          onUpdate={(patch) => updateSession(index, patch)}
                          onRemove={() => removeSession(index)}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      <button
        type="button"
        className={styles.addSessionButton}
        onClick={addSession}
        data-test-id="button-add-session"
      >
        <FontAwesomeIcon icon={faPlus} className={styles.addSessionIcon} />
        {t(`${translationPrefix}.buttons.addSession`)}
      </button>

      <ButtonToolbar className="float-right mt-5">
        <Button
          onClick={onCancel}
          variant={ButtonVariant.Secondary}
          size={ButtonSizes.Small}
          data-test-id="button-session-cancel"
        >
          {t(`${translationPrefix}.buttons.cancel`)}
        </Button>
        <Tooltip
          getPopupContainer={() =>
            document.getElementById('lmsPage') as HTMLElement
          }
          title={
            canSave || !acceptDisabledTooltip ? null : (
              <div style={{ maxWidth: '240px' }}>{acceptDisabledTooltip}</div>
            )
          }
        >
          <span style={{ display: 'inline-block' }}>{acceptButton}</span>
        </Tooltip>
      </ButtonToolbar>
    </Container>
  )
}

export default Session
